import { useCallback, useEffect, useState } from 'react';
import StoreService from '../services/storeService';
import requestDeduplicator from '../utils/requestDeduplicator';
import { normalizeEntitlements } from '../utils/storePlan';

const normalizeAccessStatus = (result) => {
  const entitlementsRaw = result?.entitlements ?? result?.Entitlements ?? null;
  const entitlements = normalizeEntitlements(entitlementsRaw);

  return {
    canAccess: Boolean(result?.canAccess ?? result?.CanAccess),
    blockReason: result?.blockReason ?? result?.BlockReason ?? null,
    entitlements,
    showReactivateNudge: Boolean(
      result?.showReactivateNudge ?? result?.ShowReactivateNudge ?? entitlements.showReactivateNudge,
    ),
    reactivateNudgeStage: result?.reactivateNudgeStage
      ?? result?.ReactivateNudgeStage
      ?? entitlements.reactivateNudgeStage,
  };
};

const accessStatusCache = new Map();
const getRequestKey = (storeId) => `store-access-status:${storeId}`;

const getCachedAccessStatus = (storeId) => {
  if (!storeId || !accessStatusCache.has(storeId)) {
    return null;
  }

  return accessStatusCache.get(storeId);
};

const setCachedAccessStatus = (storeId, status) => {
  if (!storeId || !status) {
    return;
  }

  accessStatusCache.set(storeId, status);
};

const getInitialState = (storeId, enabled) => {
  const cachedAccessStatus = enabled ? getCachedAccessStatus(storeId) : null;

  return {
    storeId: enabled ? (storeId ?? null) : null,
    accessStatus: cachedAccessStatus,
    loading: Boolean(enabled && storeId && !cachedAccessStatus),
    error: null,
  };
};

const useStoreAccessStatus = (storeId, enabled = true) => {
  const [state, setState] = useState(() => getInitialState(storeId, enabled));
  const cachedAccessStatus = enabled ? getCachedAccessStatus(storeId) : null;
  const currentStoreId = enabled ? (storeId ?? null) : null;
  const isCurrentStoreState = state.storeId === currentStoreId;
  const accessStatus = isCurrentStoreState ? state.accessStatus : cachedAccessStatus;
  const loading = enabled && Boolean(storeId) && (isCurrentStoreState ? state.loading : !cachedAccessStatus);
  const error = isCurrentStoreState ? state.error : null;

  useEffect(() => {
    let disposed = false;

    const loadAccessStatus = async () => {
      if (!enabled || !storeId) {
        setState({
          storeId: null,
          accessStatus: null,
          loading: false,
          error: null,
        });
        return;
      }

      const cachedAccessStatus = getCachedAccessStatus(storeId);
      if (cachedAccessStatus) {
        setState({
          storeId,
          accessStatus: cachedAccessStatus,
          loading: false,
          error: null,
        });
        return;
      }

      setState({
        storeId,
        accessStatus: null,
        loading: true,
        error: null,
      });

      try {
        const result = await requestDeduplicator.deduplicate(
          getRequestKey(storeId),
          () => StoreService.getAccessStatus(storeId)
        );
        const normalizedResult = normalizeAccessStatus(result);
        setCachedAccessStatus(storeId, normalizedResult);

        if (!disposed) {
          setState({
            storeId,
            accessStatus: normalizedResult,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        console.error('Failed to load store access status:', err);

        if (!disposed) {
          setState({
            storeId,
            accessStatus: null,
            loading: false,
            error: err,
          });
        }
      }
    };

    loadAccessStatus();

    return () => {
      disposed = true;
    };
  }, [enabled, storeId]);

  const refresh = useCallback(async () => {
    if (!enabled || !storeId) {
      setState({
        storeId: null,
        accessStatus: null,
        loading: false,
        error: null,
      });
      return null;
    }

    try {
      setState((prev) => ({
        storeId,
        accessStatus: prev.storeId === storeId ? prev.accessStatus : getCachedAccessStatus(storeId),
        loading: true,
        error: null,
      }));
      const result = await StoreService.getAccessStatus(storeId);
      const normalizedResult = normalizeAccessStatus(result);
      setCachedAccessStatus(storeId, normalizedResult);
      setState({
        storeId,
        accessStatus: normalizedResult,
        loading: false,
        error: null,
      });
      return normalizedResult;
    } catch (err) {
      console.error('Failed to refresh store access status:', err);
      setState({
        storeId,
        accessStatus: null,
        loading: false,
        error: err,
      });
      return null;
    }
  }, [enabled, storeId]);

  return {
    accessStatus,
    entitlements: accessStatus?.entitlements ?? null,
    loading,
    error,
    refresh,
  };
};

export { accessStatusCache, getCachedAccessStatus, setCachedAccessStatus };

export default useStoreAccessStatus;